import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { QrCode, Download, Video } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface MeetingQRCodeProps {
  meetingCode: string;
  meetingTitle: string;
  size?: number;
  fgColor?: string;
  bgColor?: string;
}

export function MeetingQRCode({ 
  meetingCode, 
  meetingTitle, 
  size = 256,
  fgColor = "hsl(262, 83%, 58%)", // Primary brand color
  bgColor = "#ffffff"
}: MeetingQRCodeProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const joinLink = `${window.location.origin}/join/${meetingCode}`;
  const logoSize = size * 0.2; // Logo is 20% of QR code size

  const downloadQRCode = () => {
    const qrContainer = document.getElementById('meeting-qr-container');
    if (!qrContainer) return;

    // Create a canvas to combine QR code and logo
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const padding = 80;
    const titleHeight = 80;
    canvas.width = size + padding * 2;
    canvas.height = size + padding * 2 + titleHeight;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Get QR code SVG
    const svg = qrContainer.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    img.onload = () => {
      // Draw QR code
      ctx.drawImage(img, padding, padding, size, size);
      
      // Draw logo container (white circle) in center
      const centerX = canvas.width / 2;
      const centerY = padding + size / 2;
      const logoCircleRadius = logoSize * 0.7;
      
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(centerX, centerY, logoCircleRadius, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw brand color background for logo
      ctx.fillStyle = fgColor;
      ctx.beginPath();
      ctx.arc(centerX, centerY, logoCircleRadius * 0.85, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw simple video icon (rectangle for screen, triangle for play)
      ctx.fillStyle = '#ffffff';
      const iconSize = logoCircleRadius * 0.8;
      const rectWidth = iconSize * 1.2;
      const rectHeight = iconSize * 0.8;
      const rectX = centerX - rectWidth / 2;
      const rectY = centerY - rectHeight / 2;
      
      // Rounded rectangle for camera
      ctx.beginPath();
      ctx.roundRect(rectX, rectY, rectWidth, rectHeight, 3);
      ctx.fill();
      
      // Add meeting title
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 24px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(meetingTitle, canvas.width / 2, size + padding + 40);
      
      // Add meeting code
      ctx.font = '18px monospace';
      ctx.fillStyle = '#666666';
      ctx.fillText(`Code: ${meetingCode}`, canvas.width / 2, size + padding + 70);
      
      // Download
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `meeting-${meetingCode}-qr.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
      
      toast({
        title: "QR Code Downloaded",
        description: "The branded QR code has been saved to your device.",
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title="Show QR Code"
        >
          <QrCode className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Meeting QR Code</DialogTitle>
          <DialogDescription>
            Scan this QR code with your mobile device to join the meeting
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* QR Code with Branded Logo */}
          <div className="flex justify-center p-8 bg-white rounded-lg shadow-inner">
            <div id="meeting-qr-container" className="relative">
              <QRCodeSVG
                id="meeting-qr-code"
                value={joinLink}
                size={size}
                level="H"
                includeMargin={true}
                fgColor={fgColor}
                bgColor={bgColor}
              />
              {/* Centered Logo Overlay */}
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-lg"
                style={{
                  width: `${logoSize}px`,
                  height: `${logoSize}px`,
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px'
                }}
              >
                <div 
                  className="w-full h-full rounded-full flex items-center justify-center"
                  style={{ 
                    background: `linear-gradient(135deg, ${fgColor}, hsl(262, 83%, 68%))` 
                  }}
                >
                  <Video 
                    className="text-white" 
                    size={logoSize * 0.5} 
                    strokeWidth={2.5}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Meeting Info */}
          <div className="space-y-3 text-center border-t pt-6">
            <div className="space-y-1">
              <p className="font-bold text-xl bg-gradient-primary bg-clip-text text-transparent">
                {meetingTitle}
              </p>
              <p className="text-sm font-mono px-4 py-2 bg-muted rounded-lg inline-block">
                Code: <span className="font-bold text-primary">{meetingCode}</span>
              </p>
            </div>
            <p className="text-xs text-muted-foreground break-all px-6 pt-2">
              {joinLink}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                navigator.clipboard.writeText(joinLink);
                toast({
                  title: "Link copied!",
                  description: "Share this link to invite others",
                });
              }}
            >
              Copy Link
            </Button>
            <Button
              variant="default"
              className="flex-1"
              onClick={downloadQRCode}
            >
              <Download className="h-4 w-4 mr-2" />
              Download QR
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
